/**
 * Public landing page for Nigastock Pro.
 * Shows hero, features, brokers, security, and waitlist signup.
 * No auth required.
 */
import { useState } from 'react';

export function LandingPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleNavigateToTrader = () => {
    window.location.href = '/trader';
  };

  const handleNavigateToFxTrader = () => {
    window.location.href = '/fx-trader';
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  return (
    <>
      <header className="site" role="banner">
        <div className="container nav">
          <a href="/" className="brand" aria-label="Nigastock home">
            <span className="brand-mark" aria-hidden="true">N</span>
            <span className="brand-name">Niga<span>stock</span> Pro</span>
          </a>
          <nav className="primary" aria-label="Primary">
            <ul>
              <li><a href="#features">Features</a></li>
              <li><a href="#how">How it works</a></li>
              <li><a href="#brokers">Brokers</a></li>
              <li><a href="#security">Security</a></li>
              <li><a href="#waitlist">Waitlist</a></li>
            </ul>
          </nav>
          <div className="nav-cta">
            <button onClick={handleNavigateToTrader} className="btn btn-ghost" id="navLaunchTrader">Equity Trader</button>
            <button onClick={handleNavigateToFxTrader} className="btn btn-ghost" id="navLaunchFxTrader">FX Trader</button>
            <a href="#waitlist" className="btn btn-primary">Get Early Access</a>
            <button className="nav-toggle" id="navToggle" aria-expanded={mobileMenuOpen} aria-controls="mobileMenu" aria-label="Open menu" onClick={toggleMobileMenu}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                <path d="M3 6h18M3 12h18M3 18h18"/>
              </svg>
            </button>
          </div>
        </div>
        <nav className={`mobile-menu ${mobileMenuOpen ? 'open' : ''}`} id="mobileMenu" aria-label="Mobile">
          <div className="container">
            <ul>
              <li><a href="#features">Features</a></li>
              <li><a href="#how">How it works</a></li>
              <li><a href="#brokers">Brokers</a></li>
              <li><a href="#security">Security</a></li>
              <li><a href="#waitlist">Waitlist</a></li>
              <li><a href="/trader"><strong style={{color: 'var(--green, #006b3c)'}}>Launch Equity Trader →</strong></a></li>
              <li><a href="/fx-trader"><strong style={{color: 'var(--green, #006b3c)'}}>Launch FX Trader →</strong> <span style={{background:'#d4a017',color:'#0a1120',fontSize:'9px',fontWeight:'800',padding:'2px 6px',borderRadius:'10px',marginLeft:'6px'}}>PRIVATE BETA</span></a></li>
            </ul>
          </div>
        </nav>
      </header>

      <main id="main">
        {/* HERO */}
        <section className="hero" aria-labelledby="hero-title">
          <div className="container hero-grid">
            <div>
              <span className="eyebrow">
                <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true"><circle cx="5" cy="5" r="4" fill="#006b3c"/></svg>
                Now in private beta · NGX equities &amp; FX
              </span>
              <h1 id="hero-title">
                Own Your Financial Future.<br/>
                Trade <em>Nigerian markets</em> — equities and FX — with speed and precision.
              </h1>
              <p className="lead">
                Nigastock Pro is the real-time trading terminal for NGX stocks, bonds, and ETFs <em>and</em> for USD/NGN spot and the African pair complex. Connect your existing broker for equities, link an FX execution venue for spot, and trade everything in seconds — all from one secure dashboard.
              </p>
              <div className="hero-ctas">
                <button onClick={handleNavigateToTrader} className="btn btn-primary btn-lg" id="heroLaunchTrader">Launch Equity Trader →</button>
                <button onClick={handleNavigateToFxTrader} className="btn btn-primary btn-lg" id="heroLaunchFxTrader" style={{position:'relative'}}>Launch FX Trader →<span style={{position:'absolute',top:'-8px',right:'-12px',background:'#d4a017',color:'#0a1120',fontSize:'9px',fontWeight:'800',padding:'2px 6px',borderRadius:'10px',letterSpacing:'0.05em'}}>PRIVATE BETA</span></button>
                <a href="#waitlist" className="btn btn-ghost btn-lg">Get Early Access</a>
              </div>
              <div className="trust-row">
                <span className="trust-pill">🔒 <b>Bank-level</b>&nbsp;security</span>
                <span className="trust-pill">⚡ Live NGX &amp; FX&nbsp;data</span>
                <span className="trust-pill">🇳🇬 SEC-aligned&nbsp;connections</span>
              </div>
            </div>

            {/* Terminal mock - Real trader image */}
            <div className="terminal" role="img" aria-label="Trader viewing real-time market charts and trading terminal">
              <img
                src="/trader-image.gif"
                alt="Professional trader analyzing live market charts on multiple screens in a trading environment"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  objectPosition: 'center',
                  borderRadius: 'inherit',
                  display: 'block'
                }}
              />
            </div>
          </div>
        </section>

        {/* FEATURES */}
        <section id="features" className="alt" aria-labelledby="feat-title">
          <div className="container">
            <div className="section-head">
              <h2 id="feat-title">Why Choose Nigastock Pro?</h2>
              <p>Everything a modern Nigerian trader needs — equities, bonds, ETFs, and FX — in one unified, professional-grade terminal.</p>
            </div>
            <div className="features">
              <div className="feature">
                <div className="icon" aria-hidden="true">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>
                </div>
                <h3>Unified Dashboard</h3>
                <p>Access all your Nigerian broker accounts — equities, bonds, cash — in one clean screen.</p>
              </div>
              <div className="feature">
                <div className="icon" aria-hidden="true">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
                </div>
                <h3>Live Market Data</h3>
                <p>Real-time prices, bids, and asks for every listed NGX stock, bond, and ETF — zero lag.</p>
              </div>
              <div className="feature">
                <div className="icon" aria-hidden="true">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3v18h18"/><path d="M7 15l4-4 4 4 5-6"/></svg>
                </div>
                <h3>Advanced Analytics</h3>
                <p>Professional charts, screeners, and 50+ technical indicators tailored for Nigerian assets.</p>
              </div>
              <div className="feature">
                <div className="icon" aria-hidden="true">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                </div>
                <h3>Secure Trading</h3>
                <p>Bank-level encryption, MFA, and SEC-compliant broker connections protect every trade.</p>
              </div>
            </div>
          </div>
        </section>

        {/* HOW IT WORKS */}
        <section id="how" aria-labelledby="how-title">
          <div className="container">
            <div className="section-head">
              <h2 id="how-title">Connect Your Broker in Minutes</h2>
              <p>Four simple steps. No new account transfers. Your broker relationship stays exactly where it is.</p>
            </div>
            <div className="steps">
              <div className="step">
                <h4>Sign Up</h4>
                <p>Create your free Nigastock Pro account with your email and verify via MFA.</p>
              </div>
              <div className="step">
                <h4>Select Your Broker</h4>
                <p>Pick from our authorized list of licensed NGX dealing member firms.</p>
              </div>
              <div className="step">
                <h4>Authorize API Access</h4>
                <p>Securely link using your broker's OAuth flow — we never see your password.</p>
              </div>
              <div className="step">
                <h4>Start Trading</h4>
                <p>Your positions and orders flow through instantly. Trade with confidence.</p>
              </div>
            </div>
          </div>
        </section>

        {/* BROKERS */}
        <section id="brokers" className="alt" aria-labelledby="brokers-title">
          <div className="container">
            <div className="section-head">
              <h2 id="brokers-title">Seamless Broker &amp; Venue Integration</h2>
              <p>For equities, connect the SEC-licensed NGX broker you already use. For FX, route through a regulated FX execution venue. Either way, your relationship with the counterparty is direct — Nigastock just makes the trading experience better.</p>
            </div>
            <div className="brokers" aria-label="Supported brokers">
              <span className="broker-chip">Stanbic IBTC Stockbrokers</span>
              <span className="broker-chip">ARM Securities</span>
              <span className="broker-chip">Meristem Securities</span>
              <span className="broker-chip">Cardinalstone</span>
              <span className="broker-chip">United Capital</span>
              <span className="broker-chip">Cordros Securities</span>
              <span className="broker-chip">Chapel Hill Denham</span>
              <span className="broker-chip">FBNQuest</span>
              <span className="broker-chip">+ more soon</span>
            </div>

            <div className="broker-ctas">
              <button onClick={handleNavigateToTrader} className="btn btn-primary btn-lg">Connect Your Broker Now</button>
              <a href="#broker-partners" className="btn btn-ghost btn-lg">Are you a broker? Partner with us →</a>
            </div>
          </div>
        </section>

        {/* SECURITY */}
        <section id="security" className="alt" aria-labelledby="sec-title">
          <div className="container">
            <div className="security">
              <h2 id="sec-title">Your Security is Non-Negotiable</h2>
              <p>Nigastock Pro is built on a defense-in-depth architecture — from the network edge to the database. Your credentials never leave your broker. Your data stays encrypted end-to-end.</p>
              <div className="sec-grid">
                <div className="sec-card">
                  <h4>🔐 End-to-End Encryption</h4>
                  <p>TLS 1.3 in transit. AES-256 at rest. KMS envelope encryption for every piece of PII.</p>
                </div>
                <div className="sec-card">
                  <h4>🪪 OAuth Broker Links</h4>
                  <p>We authorize via broker-issued tokens. No passwords stored. Revoke access anytime.</p>
                </div>
                <div className="sec-card">
                  <h4>🛡️ Edge Protection</h4>
                  <p>WAF, DDoS shielding, rate limiting, and bot filtering guard every API request.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* WAITLIST */}
        <section id="waitlist" aria-labelledby="waitlist-title">
          <div className="container">
            <div className="waitlist-wrap">
              <h2 id="waitlist-title">Get Early Access</h2>
              <p>Join our waitlist to be among the first to trade on Nigastock Pro.</p>
              <form className="waitlist" onSubmit={(e) => {
                e.preventDefault();
                alert('Thank you for your interest! We will contact you soon.');
              }}>
                <div>
                  <label htmlFor="wl-email">Email <span className="req" aria-hidden="true">*</span></label>
                  <input id="wl-email" name="email" type="email" required maxLength={120} placeholder="you@example.com" />
                </div>
                <div>
                  <label htmlFor="wl-name">Full Name <span className="req" aria-hidden="true">*</span></label>
                  <input id="wl-name" name="name" type="text" required maxLength={80} placeholder="Your name" />
                </div>
                <div className="full">
                  <button type="submit" className="btn btn-primary btn-lg">Join Waitlist</button>
                </div>
              </form>
            </div>
          </div>
        </section>
      </main>

      {/* FOOTER */}
      <footer>
        <div className="container foot-grid">
          <div>
            <h5>Nigastock Pro</h5>
            <p>The premier trading terminal for Nigerian securities. Trade equities, bonds, ETFs, and FX with institutional-grade precision.</p>
          </div>
          <div>
            <h5>Product</h5>
            <ul>
              <li><a href="/trader">Equity Trader</a></li>
              <li><a href="/fx-trader">FX Trader</a></li>
              <li><a href="#features">Features</a></li>
              <li><a href="#security">Security</a></li>
            </ul>
          </div>
          <div>
            <h5>Company</h5>
            <ul>
              <li><a href="mailto:hello@nigastock.ng">Contact</a></li>
              <li><a href="#">Blog</a></li>
              <li><a href="#">Careers</a></li>
            </ul>
          </div>
          <div>
            <h5>Legal</h5>
            <ul>
              <li><a href="./legal/privacy.html">Privacy</a></li>
              <li><a href="./legal/terms.html">Terms</a></li>
              <li><a href="./legal/fx-risk.html">FX Risk</a></li>
            </ul>
          </div>
        </div>
        <div className="container legal">
          <div>&copy; 2024 Nigastock Technologies Ltd. All rights reserved.</div>
          <div>
            <a href="./legal/privacy.html">Privacy Policy</a> · <a href="./legal/terms.html">Terms of Service</a>
          </div>
        </div>
      </footer>
    </>
  );
}
